import React, { useEffect, useState } from "react";
import api, { rupees } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Loading, Pill, BrandDot, Kpi } from "@/components/common";
import { toast } from "sonner";
import { Banknote, AlertCircle } from "lucide-react";

export default function Redemptions() {
  const { selectedBrand } = useApp();
  const [payouts, setPayouts] = useState(null);
  const canFinance = true;

  const load = () => {
    const b = selectedBrand === "ALL" ? "" : `?brand=${selectedBrand}`;
    api.get(`/payouts${b}`).then((r) => setPayouts(r.data)).catch(() => setPayouts({ error: true }));
  };
  useEffect(() => { load(); }, [selectedBrand]);

  const reconcile = async (p, outcome) => {
    try { await api.post(`/payouts/${p.id}/reconcile?outcome=${outcome}`); toast.success(`Payout ${outcome}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!payouts) return <Loading />;
  if (payouts.error) return <div><PageHead title="Redemptions & Payouts" /><div className="bg-rose-50 border border-rose-100 rounded-xl p-6 text-rose-700 text-sm">Finance access required for this screen.</div></div>;

  return (
    <div>
      <PageHead overline="Finance Operations" title="Redemptions & Payouts" desc="Reward disbursement to UPI/bank via sandbox rail. Reservations held until reconciled. Points liability is separate from provider cash balance." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi testid="kpi-disbursed" label="Disbursed" value={rupees(payouts.disbursed_paise)} icon={Banknote} accent="#15803D" />
        <Kpi testid="kpi-pending" label="Processing" value={payouts.summary.processing} accent="#F59E0B" />
        <Kpi testid="kpi-failed" label="Failed" value={payouts.summary.failed} accent="#EF4444" />
        <Kpi testid="kpi-succeeded" label="Succeeded" value={payouts.summary.succeeded} accent="#10B981" />
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-sm text-amber-800 mb-5 flex items-center gap-2">
        <AlertCircle size={15} /> Provider cash balance: <span className="font-semibold">{payouts.provider_balance}</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider"><tr><th className="text-left px-4 py-3">Brand</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">UTR</th><th className="text-left px-4 py-3">Created</th><th className="text-right px-4 py-3">Reconcile</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {payouts.payouts.map((p) => (
              <tr key={p.id} data-testid={`payout-${p.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3"><BrandDot id={p.brand_id} />{p.brand_id}</td>
                <td className="px-4 py-3 text-right font-semibold">{rupees(p.amount_paise)}</td>
                <td className="px-4 py-3"><Pill status={p.status}>{p.status}</Pill></td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.utr || "—"}</td>
                <td className="px-4 py-3 text-slate-400 text-xs">{p.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  {["processing", "queued", "unknown"].includes(p.status) ? (
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`payout-succeed-${p.id}`} onClick={() => reconcile(p, "succeeded")} className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded">Mark success</button>
                      <button data-testid={`payout-fail-${p.id}`} onClick={() => reconcile(p, "failed")} className="text-xs font-semibold text-rose-700 bg-rose-50 px-2 py-1 rounded">Mark failed</button>
                    </div>
                  ) : <span className="text-xs text-slate-300">final</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
